import React from 'react';
import styled from 'types/theme.types';
import { useIntl } from 'react-intl';
import { reportError } from 'utils/sentry.utils';
import { useMount } from 'ahooks';
import isString from 'lodash/isString';
import { ReactComponent as SomethingWentWrongSVG } from 'assets/images/something-went-wrong.svg';

interface Props {
  errorExtraInfo?: Record<string, unknown> & { reason?: string };
  errorToReport?: any; // give this prop only if you want to report it to Sentry
  message?: string;
  messageValues?: Record<string, string>;
  icon?: React.ReactElement;
  className?: string;
}

const unexpectedErrorId = 'notifications.error.something.went.wrong';

const ErrorContainer: React.FunctionComponent<Props> = ({
  errorToReport,
  errorExtraInfo,
  message = unexpectedErrorId,
  messageValues,
  icon,
  className
}) => {
  const intl = useIntl();
  const translatedMessage = message && intl.formatMessage({ id: message, defaultMessage: message }, messageValues);

  useMount(() => {
    if (errorToReport) {
      reportError('ErrorContainer', errorToReport, {
        path: window.location.href,
        message: translatedMessage,
        ...errorExtraInfo
      });
    }
  });

  return (
    <Container data-e2e="error-container" className={className}>
      {icon || <StyledSomethingWentWrongIcon />}
      <EmptyLayoutMessage data-e2e="error-container-actual-msg">{translatedMessage}</EmptyLayoutMessage>
      {isString(errorExtraInfo?.reason) && <span>{errorExtraInfo!.reason}</span>}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100% - 46px);
  width: 100%;
  color: ${({ theme }) => theme.textGray};
`;

const EmptyLayoutMessage = styled.h2`
  font-size: 24px;
  white-space: pre-line;
`;

const StyledSomethingWentWrongIcon = styled(SomethingWentWrongSVG)`
  width: 200px;
  height: 200px;
`;

export default ErrorContainer;
